// @ts-nocheck
import React, { useState, useEffect, useRef, useMemo } from "react";
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import { useBusiness } from "../contexts/BusinessContext";
import { useAuth } from '../hooks/useAuth';
import { MessageCircle, Mail, Phone, Clock, User, Edit, Plus, ChevronDown, Building2, Calendar, CheckCircle, FileText, Eye, ArrowLeft } from "lucide-react";
import { supabase } from "../lib/supabase";
import EditCustomerModal from "../components/manage/EditCustomerModal";
import CustomerModal from "../components/CustomerModal";
import BookingInfoModal from "../components/BookingInfoModal";
import BetaPaymentsModal from "../components/BetaPaymentsModal";
import ProposalDetailsModal from "../components/ProposalDetailsModal";
import { Customer } from "../lib/customers";
import { sendSMS } from "../lib/messaging";
import { sendGmailEmail } from "../lib/email";
import { notifyNewMessage } from "../lib/notificationSender";

import { EmailReplyComposer } from "../components/email/EmailReplyComposer";
import { EmailDisplay } from "../components/email/EmailDisplay";
import { SMSReplyComposer } from "../components/SMSReplyComposer";
import { parseEmailContent, getEmailPreview } from "../lib/emailParser";
import { getCustomerMilestones, BookingMilestone } from "../lib/bookingMilestones";
import { getBooking, Booking } from "../lib/bookings";
import { getProposal, Proposal } from "../lib/proposals";
import { NexusMessagesAssistant } from "../components/NexusMessagesAssistant";

interface Message {
  id: string;
  business_id: string;
  customer_id: string | null;
  message_type: 'sms' | 'email' | 'call';
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  body: string;
  subject?: string;
  read: 'read' | 'unread';
  created_at: string;
  updated_at: string;
  ai_response?: boolean;
  // Call-specific fields
  call_status?: 'answered' | 'missed' | 'ringing' | 'completed';
  duration_seconds?: number;
  call_sid?: string;
  viewed?: boolean;
}

interface CommunicationCard {
  id: string; // unique identifier for grouping
  type: 'customer' | 'phone' | 'email';
  customer_id?: string;
  customer_name?: string;
  contact_info: string; // phone or email
  messages: Message[];
  last_message: Message;
  unread_count: number;
  has_unread: boolean;
}

// Timeline item that can be either a message or milestone
interface TimelineItem {
  id: string;
  type: 'message' | 'milestone';
  created_at: string;
  message?: Message;
  milestone?: BookingMilestone;
}

export default function Messages() {
  const { selectedBusiness } = useBusiness();
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'unread' | 'read'>('unread');
  const [messages, setMessages] = useState<Message[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [callHistory, setCallHistory] = useState<any[]>([]);
  const [communicationCards, setCommunicationCards] = useState<CommunicationCard[]>([]);
  const [loading, setLoading] = useState(false); // Start with false for instant display
  
  // URL parameter handling for customer selection
  const [urlCustomerName, setUrlCustomerName] = useState<string | null>(null);
  const [urlCustomerId, setUrlCustomerId] = useState<string | null>(null);
  const [cameFromCRM, setCameFromCRM] = useState(false);
  const [cameFromBooking, setCameFromBooking] = useState(false);
  const [bookingId, setBookingId] = useState<string | null>(null);
  const [sourcePage, setSourcePage] = useState<string | null>(null);

  // Parse URL parameters on component mount
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const customerId = searchParams.get('customerId');
    const source = searchParams.get('source');
    const proposalId = searchParams.get('proposalId');
    
    if (customerId) {
      setUrlCustomerId(customerId);
    }
    
    if (source) {
      setSourcePage(source);
      setCameFromCRM(source === 'crm');
      setCameFromBooking(source === 'bookings' || source === 'calendar');
    }
    
    if (proposalId) {
      // Handle proposal context if needed
    }
  }, [location.search]);
  

  
  // Add dedicated loading state for communication cards
  const [cardsLoading, setCardsLoading] = useState(false); // Start false for instant display
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  
  const [selectedCard, setSelectedCard] = useState<CommunicationCard | null>(null);
  const [editCustomerModalOpen, setEditCustomerModalOpen] = useState(false);
  const [createCustomerModalOpen, setCreateCustomerModalOpen] = useState(false);
  const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState<Customer | null>(null);
  const [createCustomerInitialData, setCreateCustomerInitialData] = useState<{ phone?: string; email?: string }>({});

  // Track locally marked-as-read messages for instant UI updates
  const [locallyReadMessages, setLocallyReadMessages] = useState<Set<string>>(new Set());
  
  // Milestones for customers
  const [customerMilestones, setCustomerMilestones] = useState<{[customerId: string]: BookingMilestone[]}>({});
  
  // Booking info modal state
  const [bookingInfoModalOpen, setBookingInfoModalOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  
  // Beta payments modal state
  const [betaPaymentsModalOpen, setBetaPaymentsModalOpen] = useState(false);
  const [selectedBookingForPayments, setSelectedBookingForPayments] = useState<Booking | null>(null);
  
  // Proposal details modal state
  const [proposalDetailsModalOpen, setProposalDetailsModalOpen] = useState(false);
  const [selectedProposal, setSelectedProposal] = useState<Proposal | null>(null);
  
  // Pipeline pagination state
  const CARDS_PAGE_SIZE = 10; // Reduced for better testing and UX
  const [pipelineOffset, setPipelineOffset] = useState(CARDS_PAGE_SIZE);
  const [loadingMoreCards, setLoadingMoreCards] = useState(false);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileModalOpen, setMobileModalOpen] = useState(false);



  // Check if a conversation has actual unread messages (real-time check)
  const hasUnreadMessages = (card: CommunicationCard): boolean => {
    const unreadMessages = card.messages.filter(msg => 
      msg.read === 'unread' && !locallyReadMessages.has(msg.id)
    );
    
    console.log('🤖 Real-time unread check for', card.id, {
      totalMessages: card.messages.length,
      unreadCount: unreadMessages.length,
      locallyReadCount: locallyReadMessages.size,
      hasUnread: unreadMessages.length > 0
    });
    
    return unreadMessages.length > 0;
  };



  // Mark message as read (for individual message unread badges)
  const markMessageAsRead = async (messageId: string) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      console.log('[UNREAD BADGE] Marking message/call as read:', messageId);

      // Check if this is a call or a message
      const callMessage = [...callHistory].find(call => call.id === messageId);
      
      if (callMessage) {
        // This is a call - update call_history table
        console.log('[UNREAD BADGE] Marking call as viewed:', messageId);
        
        const { error } = await supabase
          .from('call_history')
          .update({ viewed: true })
          .eq('id', messageId);

        if (error) {
          console.error('Failed to mark call as viewed:', error);
          throw error;
        }

        // Update local call history state
        setCallHistory(prevHistory => 
          prevHistory.map(call => 
            call.id === messageId ? { ...call, viewed: true } : call
          )
        );

        console.log('[UNREAD BADGE] Successfully marked call as viewed:', messageId);
      } else {
        // This is a regular message - update all_messages table
        console.log('[UNREAD BADGE] Marking message as read:', messageId);

        // Immediately add to locally read set for instant UI update
        setLocallyReadMessages(prev => new Set([...prev, messageId]));

        // Update database
        const { error } = await supabase
          .from('all_messages')
          .update({ read: 'read' })
          .eq('id', messageId);

        if (error) {
          console.error('Database update failed, reverting local state:', error);
          // Remove from locally read set if database update fails
          setLocallyReadMessages(prev => {
            const newSet = new Set(prev);
            newSet.delete(messageId);
            return newSet;
          });
          throw error;
        }

        console.log('[UNREAD BADGE] Successfully marked message as read:', messageId);
      }
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  // Mark all messages in a conversation as read
  const markAllMessagesAsRead = async (card: CommunicationCard) => {
    try {
      if (!supabase) {
        throw new Error('Supabase client not initialized');
      }

      // Get all unread message IDs for this conversation
      const unreadMessageIds = card.messages
        .filter(msg => msg.read === 'unread' && msg.direction === 'inbound')
        .map(msg => msg.id);

      if (unreadMessageIds.length === 0) return;

      console.log('[UNREAD BADGE] Marking multiple messages as read:', unreadMessageIds);

      // Immediately add all to locally read set for instant UI update
      setLocallyReadMessages(prev => new Set([...prev, ...unreadMessageIds]));

      // Update database
      const { error } = await supabase
        .from('all_messages')
        .update({ read: 'read' })
        .in('id', unreadMessageIds);

      if (error) {
        console.error('Database update failed, reverting local state:', error);
        // Remove all from locally read set if database update fails
        setLocallyReadMessages(prev => {
          const newSet = new Set(prev);
          unreadMessageIds.forEach(id => newSet.delete(id));
          return newSet;
        });
        throw error;
      }

      console.log('[UNREAD BADGE] Successfully marked multiple as read:', unreadMessageIds);
      
      // Dispatch custom event to notify navigation badge
      window.dispatchEvent(new CustomEvent('unreadMessagesChanged'));
    } catch (error) {
      console.error('Error marking all messages as read:', error);
    }
  };

  // Handle edit customer button click
  const handleEditCustomer = (customer: Customer) => {
    setSelectedCustomerForEdit(customer);
    setEditCustomerModalOpen(true);
  };

  // Handle create customer button click for phone/email
  const handleCreateCustomer = (card: CommunicationCard) => {
    const initialData: { phone?: string; email?: string } = {};
    
    if (card.type === 'phone') {
      initialData.phone = card.contact_info;
    } else if (card.type === 'email') {
      initialData.email = card.contact_info;
    }
    
    setCreateCustomerInitialData(initialData);
    setCreateCustomerModalOpen(true);
  };

  // Handle customer modal success
  const handleCustomerSuccess = (customer: Customer) => {
    // Refresh data to show updated customer info
    if (selectedBusiness?.id) {
      const fetchData = async () => {
        try {
          if (!supabase) {
            throw new Error('Supabase client not initialized');
          }

          // Fetch customers
          const { data: customersData, error: customersError } = await supabase
            .from('customers')
            .select('*')
            .eq('business_id', selectedBusiness.id);

          if (customersError) throw customersError;
          setCustomers(customersData || []);
        } catch (error) {
          console.error('Error fetching customers:', error);
        }
      };
      fetchData();
    }
    
    // Close modals
    setEditCustomerModalOpen(false);
    setCreateCustomerModalOpen(false);
    setSelectedCustomerForEdit(null);
    setCreateCustomerInitialData({});
  };

  // Handle milestone click to open booking info or proposal details
  const handleMilestoneClick = async (milestone: BookingMilestone) => {
    try {
      // For milestones, the booking_id is stored in the booking_id field
      // But since we're using it for both bookings and proposals, we need to check the metadata
      const entityId = milestone.booking_id;
      
      // Check if this is a proposal milestone
      if (milestone.milestone.metadata?.entity_type === 'proposal') {
        console.log('Loading proposal for milestone:', entityId);
        const proposal = await getProposal(entityId);
        if (proposal) {
          setSelectedProposal(proposal);
          setProposalDetailsModalOpen(true);
        } else {
          console.error('Proposal not found:', entityId);
        }
        return;
      }
      
      // Handle booking milestones
      if (entityId) {
        console.log('Loading booking for milestone:', entityId);
        const booking = await getBooking(entityId);
        if (booking) {
          setSelectedBooking(booking);
          setBookingInfoModalOpen(true);
        } else {
          console.error('Booking not found:', entityId);
        }
      }
    } catch (error) {
      console.error('Error loading entity from milestone:', error);
    }
  };

  // Fetch milestones for a specific customer (lazy loading)
  const fetchCustomerMilestones = async (customerId: string, setLoading?: (loading: boolean) => void) => {
    try {
      console.log('Fetching milestones for customer:', customerId);
      if (setLoading) setLoading(true);
      
      const milestones = await getCustomerMilestones(customerId);
      setCustomerMilestones(prev => ({
        ...prev,
        [customerId]: milestones
      }));
    } catch (error) {
      console.error(`Error fetching milestones for customer ${customerId}:`, error);
    } finally {
      if (setLoading) setLoading(false);
    }
  };

  // Handle booking info modal update
  const handleBookingUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Handle proposal details modal update
  const handleProposalUpdate = () => {
    // Refresh milestones for the selected customer only
    if (selectedCard?.customer_id) {
      fetchCustomerMilestones(selectedCard.customer_id);
    }
  };

  // Handle back to CRM navigation
  const handleBackToCRM = () => {
    navigate('/crm');
  };

  // Handle back to booking navigation
  const handleBackToBooking = async () => {
    if (sourcePage === 'calendar' && bookingId) {
      // For calendar, open the modal immediately using a portal
      try {
        // Import the required modules
        const [{ getBooking }, { createPortal }, BookingInfoModalModule] = await Promise.all([
          import('../lib/bookings'),
          import('react-dom'),
          import('../components/BookingInfoModal')
        ]);
        
        const BookingInfoModal = BookingInfoModalModule.default;
        
        // Fetch booking data
        const booking = await getBooking(bookingId);
        
        // Create a modal container
        const modalContainer = document.createElement('div');
        modalContainer.id = 'instant-booking-modal';
        document.body.appendChild(modalContainer);
        
        // Render the modal immediately
        const { createRoot } = await import('react-dom/client');
        const root = createRoot(modalContainer);
        
        root.render(
          React.createElement(BookingInfoModal, {
            isOpen: true,
            onClose: () => {
              root.unmount();
              document.body.removeChild(modalContainer);
              // Navigate to calendar after closing
              navigate('/calendarV2');
            },
            booking: booking,
            onUpdate: () => {},
            readOnly: false
          })
        );
        
        // Navigate to calendar in background
        setTimeout(() => {
          navigate('/calendarV2', { 
            state: { selectedBookingId: bookingId, skipModalOpen: true } 
          });
        }, 100);
        
      } catch (error) {
        console.error('Error opening instant modal:', error);
        // Fallback to normal navigation
        navigate('/calendarV2', { 
          state: { selectedBookingId: bookingId } 
        });
      }
    } else if (sourcePage === 'crm') {
      // Navigate back to CRM
      navigate('/crm');
    } else {
      // Default: Navigate back to bookings page
      navigate('/bookings');
    }
  };

  // Load more cards function
  const loadMoreCards = () => {
    if (loadingMoreCards || !hasMoreCards) return;
    
    setLoadingMoreCards(true);
    
    // Simulate brief loading for better UX
    setTimeout(() => {
      setPipelineOffset(prev => prev + CARDS_PAGE_SIZE);
      setLoadingMoreCards(false);
    }, 300);
  };

  // Reset pipeline pagination when switching tabs
  useEffect(() => {
    setPipelineOffset(CARDS_PAGE_SIZE);
  }, [activeTab]);
  
  // Handle window resize for mobile detection
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);



  // Optimized real-time loading strategy
  useEffect(() => {
    if (!selectedBusiness?.id) return;

    const fetchData = async () => {
      try {
        if (!supabase) {
          throw new Error('Supabase client not initialized');
        }

        // STRATEGY: Load critical data with optimized queries
        // 1. Use indexed fields for faster queries
        // 2. Load minimal data first, expand later
        // 3. Use efficient pagination
        // OPTIMIZATION: Load only the most critical data with minimal fields
        const [messagesResult, customersResult] = await Promise.all([
          // Load recent messages with essential fields only
          supabase
            .from('all_messages')
            .select('id, customer_id, message_type, direction, from_number, to_number, body, subject, read, created_at')
            .eq('business_id', selectedBusiness.id)
            .order('created_at', { ascending: false })
            .limit(100), // Load more messages to enable proper pagination
          // Load customers with minimal fields
          supabase
            .from('customers')
            .select('id, first_name, last_name, phone, email, is_supplier')
            .eq('business_id', selectedBusiness.id)
        ]);

        if (!messagesResult.error && !customersResult.error) {
          setMessages(messagesResult.data || []);
          setCustomers(customersResult.data || []);
          setInitialDataLoaded(true);
          setCardsLoading(false);
        }

        // Load call history separately and quickly
        setTimeout(async () => {
          const callHistoryResult = await supabase
            .from('call_history')
            .select('*')
            .eq('business_id', selectedBusiness.id)
            .order('call_time', { ascending: false })
            .limit(20); // Very recent calls only
            
          if (!callHistoryResult.error) {
            setCallHistory(callHistoryResult.data || []);
          }
        }, 10); // Load calls almost immediately after messages

        setPipelineOffset(CARDS_PAGE_SIZE);
        setLocallyReadMessages(new Set());
        
      } catch (error) {
        console.error('Error fetching messages:', error);
        setCardsLoading(false);
      }
    };

    fetchData();
  }, [selectedBusiness?.id]);

  // Handle URL parameters for customer selection
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const customerParam = urlParams.get('customer');
    const customerIdParam = urlParams.get('customerId');
    const bookingIdParam = urlParams.get('bookingId');
    const sourceParam = urlParams.get('source');
    
    if (customerParam) {
      setUrlCustomerName(decodeURIComponent(customerParam));
    }
    
    if (customerIdParam) {
      setUrlCustomerId(customerIdParam);
      
      if (bookingIdParam) {
        // If we have both customerId and bookingId, we came from a booking
        setBookingId(bookingIdParam);
        setSourcePage(sourceParam || 'bookings'); // default to bookings if no source specified
        setCameFromBooking(true);
      } else {
        // If we only have customerId, we came from CRM
        setCameFromCRM(true);
      }
    }
  }, []);

  // Auto-select customer from URL parameter when communication cards are loaded
  useEffect(() => {
    if ((urlCustomerName || urlCustomerId) && communicationCards.length > 0 && !selectedCard) {
      let customerCard = null;
      
      // First try to find by customer ID (more precise)
      if (urlCustomerId) {
        customerCard = communicationCards.find(card => 
          card.customer_id === urlCustomerId
        );
        
        if (customerCard) {
          console.log('Auto-selecting customer from URL by ID:', urlCustomerId);
          setSelectedCard(customerCard);
          // Clear the URL parameter to prevent re-selection on page refresh
          const url = new URL(window.location.href);
          url.searchParams.delete('customerId');
          window.history.replaceState({}, '', url.toString());
          return;
        }
      }
      
      // Fallback to customer name search
      if (urlCustomerName && !customerCard) {
        customerCard = communicationCards.find(card => 
          card.customer_name && card.customer_name.toLowerCase().includes(urlCustomerName.toLowerCase())
        );
        
        if (customerCard) {
          console.log('Auto-selecting customer from URL by name:', urlCustomerName);
          setSelectedCard(customerCard);
          // Clear the URL parameter to prevent re-selection on page refresh
          const url = new URL(window.location.href);
          url.searchParams.delete('customer');
          window.history.replaceState({}, '', url.toString());
        }
      }
    }
  }, [urlCustomerName, urlCustomerId, communicationCards, selectedCard]);

  // Set up realtime subscription for live updates
  useEffect(() => {
    if (!selectedBusiness?.id || !supabase) return;

    console.log('[REALTIME] Setting up subscription for business:', selectedBusiness.id);

    // Create the subscription for messages
    const subscription = supabase
      .channel(`communications_${selectedBusiness.id}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'all_messages',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to messages for this business
        },
        (payload) => {
          console.log('[REALTIME] Received event:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            const newMessage = payload.new as Message;
            console.log('[REALTIME] 📥 New message received:', {
              id: newMessage.id,
              type: newMessage.message_type,
              direction: newMessage.direction,
              from: newMessage.from_number,
              to: newMessage.to_number,
              body: newMessage.body.substring(0, 50) + '...',
              timestamp: newMessage.created_at
            });
            
            // Send push notification for inbound messages (from customers)
            if (newMessage.direction === 'inbound' && selectedBusiness?.id) {
              console.log('🔔 [PUSH NOTIFICATION] Attempting to send push notification for inbound message:', {
                messageId: newMessage.id,
                direction: newMessage.direction,
                businessId: selectedBusiness.id,
                messageType: newMessage.message_type
              });
              
              const customerName = newMessage.first_name && newMessage.last_name 
                ? `${newMessage.first_name} ${newMessage.last_name}`
                : 'Customer';
              
              const channelType = newMessage.message_type === 'email' ? 'email' : 'sms';
              const messagePreview = newMessage.content || newMessage.body || 'New message received';
              
              console.log('🔔 [PUSH NOTIFICATION] Sending notification:', {
                customerName,
                channelType,
                messagePreview: messagePreview.substring(0, 50) + '...',
                customerId: newMessage.customer_id || newMessage.communication_id
              });
              
              // Send push notification (async, don't block UI)
              notifyNewMessage(
                selectedBusiness.id,
                newMessage.customer_id || newMessage.communication_id || '',
                customerName,
                messagePreview,
                channelType
              ).then(success => {
                console.log('🔔 [PUSH NOTIFICATION] Result:', success ? 'SUCCESS' : 'FAILED');
              }).catch(error => {
                console.error('🔔 [PUSH NOTIFICATION] Failed to send push notification for message:', error);
              });
            } else {
              console.log('🔔 [PUSH NOTIFICATION] Skipping notification:', {
                direction: newMessage.direction,
                businessId: selectedBusiness?.id,
                reason: newMessage.direction !== 'inbound' ? 'Not inbound message' : 'No business ID'
              });
            }
            
            setMessages(prevMessages => {
              // Check if message already exists (prevent duplicates)
              if (prevMessages.some(msg => msg.id === newMessage.id)) {
                console.log('[REALTIME] ⚠️ Message already exists, skipping:', newMessage.id);
                return prevMessages;
              }
              
              console.log('[REALTIME] ✅ Adding message to state, total messages will be:', prevMessages.length + 1);
              
              // Add new message and sort by created_at (newest first for the state)
              const updatedMessages = [newMessage, ...prevMessages].sort(
                (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
              );
              
              return updatedMessages;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedMessage = payload.new as Message;
            console.log('[REALTIME] Updating message:', updatedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.map(msg =>
                msg.id === updatedMessage.id ? updatedMessage : msg
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedMessage = payload.old as Message;
            console.log('[REALTIME] Removing message:', deletedMessage.id);
            
            setMessages(prevMessages =>
              prevMessages.filter(msg => msg.id !== deletedMessage.id)
            );
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'call_history',
          filter: `business_id=eq.${selectedBusiness.id}` // Only listen to calls for this business
        },
        (payload) => {
          console.log('[REALTIME] Call history event:', payload.eventType, payload);

          if (payload.eventType === 'INSERT') {
            const newCall = payload.new;
            console.log('[REALTIME] 📞 New call received:', newCall);
            
            setCallHistory(prevCalls => {
              // Check if call already exists (prevent duplicates)
              if (prevCalls.some(call => call.id === newCall.id)) {
                console.log('[REALTIME] ⚠️ Call already exists, skipping:', newCall.id);
                return prevCalls;
              }
              
              // Add new call and sort by call_time (newest first)
              const updatedCalls = [newCall, ...prevCalls].sort(
                (a, b) => new Date(b.call_time).getTime() - new Date(a.call_time).getTime()
              );
              
              return updatedCalls;
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedCall = payload.new;
            console.log('[REALTIME] 📞 Updating call:', updatedCall.call_sid);
            
            setCallHistory(prevCalls =>
              prevCalls.map(call =>
                call.id === updatedCall.id ? updatedCall : call
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedCall = payload.old;
            console.log('[REALTIME] 📞 Removing call:', deletedCall.call_sid);
            
            setCallHistory(prevCalls =>
              prevCalls.filter(call => call.id !== deletedCall.id)
            );
          }
        }
      )
      .subscribe((status) => {
        console.log('[REALTIME] Subscription status:', status);
      });

    // Cleanup subscription on unmount or business change
    return () => {
      console.log('[REALTIME] Cleaning up subscription');
      subscription.unsubscribe();
    };
  }, [selectedBusiness?.id]);



  // Optimized communication cards processing
  useEffect(() => {
    if (!messages.length && !callHistory.length) {
      setCommunicationCards([]);
      return;
    }

    // Use Map for O(1) lookups instead of object
    const cards = new Map<string, CommunicationCard>();
    
    // Create customer lookup map for fast access
    const customerMap = new Map(customers.map(c => [c.id, c]));

    // Convert calls to message format for unified processing
    const callMessages: Message[] = callHistory.map((call) => {
      // Match customer by phone number if customer_id is not set
      let matchedCustomerId = call.customer_id;
      
      if (!matchedCustomerId) {
        // Try to find customer by phone number
        const callPhoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
        const matchedCustomer = customers.find(customer => {
          if (!customer.phone) return false;
          // Normalize phone numbers for comparison
          const customerPhone = customer.phone.replace(/\D/g, '');
          const callPhone = callPhoneNumber.replace(/\D/g, '');
          return customerPhone === callPhone || 
                 customerPhone === callPhone.substring(1) || // Remove country code
                 `1${customerPhone}` === callPhone; // Add country code
        });
        
        if (matchedCustomer) {
          matchedCustomerId = matchedCustomer.id;
          console.log(`📞 Matched call to customer: ${matchedCustomer.first_name} ${matchedCustomer.last_name}`);
        }
      }
      
      return {
        id: call.id,
        business_id: call.business_id,
        customer_id: matchedCustomerId,
        message_type: 'call' as const,
        direction: call.direction,
        from_number: call.from_number,
        to_number: call.to_number,
        body: call.direction === 'outbound' ? 'Outbound call' : 
              (call.status === 'answered' ? 'Call answered' : 'Missed call'),
        read: 'read' as const, // Calls are always considered "read"
        created_at: call.call_time,
        updated_at: call.updated_at || call.call_time,
        call_status: call.status,
        duration_seconds: call.duration_seconds,
        call_sid: call.call_sid,
        viewed: call.viewed
      };
    });

    // Combine messages and calls
    const allCommunications = [...messages, ...callMessages];

    allCommunications.forEach((message) => {
      let cardId: string;
      let cardType: 'customer' | 'phone' | 'email';
      let contactInfo: string;
      let customerName: string | undefined;

      if (message.customer_id) {
        // Group by customer
        cardId = `customer_${message.customer_id}`;
        cardType = 'customer';
        const customer = customerMap.get(message.customer_id);
        customerName = customer ? `${customer.first_name} ${customer.last_name}`.trim() : undefined;
        contactInfo = message.message_type === 'sms' 
          ? (message.direction === 'inbound' ? message.from_number : message.to_number)
          : (message.direction === 'inbound' ? message.from_number : message.to_number);
      } else {
        // Group by contact info (phone or email)
        const contact = message.direction === 'inbound' ? message.from_number : message.to_number;
        cardId = `${message.message_type}_${contact}`;
        cardType = message.message_type === 'sms' ? 'phone' : 
                  message.message_type === 'call' ? 'phone' : 'email';
        contactInfo = contact;
        customerName = undefined;
      }

      let card = cards.get(cardId);
      if (!card) {
        card = {
          id: cardId,
          type: cardType,
          customer_id: message.customer_id || undefined,
          customer_name: customerName,
          contact_info: contactInfo,
          messages: [],
          last_message: message,
          unread_count: 0,
          has_unread: false,
        };
        cards.set(cardId, card);
      }

      card.messages.push(message);
      
      // Update last message if this one is newer (using timestamp comparison for speed)
      const messageTime = new Date(message.created_at).getTime();
      const lastMessageTime = new Date(card.last_message.created_at).getTime();
      if (messageTime > lastMessageTime) {
        card.last_message = message;
      }

      // Count unread efficiently
      if (message.read === 'unread' && !locallyReadMessages.has(message.id)) {
        card.unread_count++;
        card.has_unread = true;
      } else if (message.message_type === 'call' && message.call_status === 'missed' && !message.viewed) {
        card.unread_count++;
        card.has_unread = true;
      }
    });

    // Convert to array and sort efficiently
    const cardsArray = Array.from(cards.values()).sort(
      (a, b) => new Date(b.last_message.created_at).getTime() - new Date(a.last_message.created_at).getTime()
    );

    console.log('[REALTIME] 📊 Communication cards updated:', {
      totalCards: cardsArray.length,
      selectedCardId: selectedCardIdRef.current,
      selectedCardExists: selectedCardIdRef.current ? cardsArray.some(card => card.id === selectedCardIdRef.current) : false,
      selectedCardMessages: selectedCardIdRef.current ? 
        cardsArray.find(card => card.id === selectedCardIdRef.current)?.messages.length : 'N/A'
    });

    setCommunicationCards(cardsArray);
    
    // Reset pipeline pagination when communication cards change significantly
    const previousCardsLength = communicationCards.length;
    if (Math.abs(cardsArray.length - previousCardsLength) > 5) {
      setPipelineOffset(CARDS_PAGE_SIZE);
    }
  }, [messages, callHistory, customers, locallyReadMessages]);

  // Keep track of selectedCard ID to avoid stale closure issues
  const selectedCardIdRef = useRef<string | null>(null);
  
  // Update selectedCard ID when it changes
  useEffect(() => {
    selectedCardIdRef.current = selectedCard?.id || null;
    
    // Set localStorage flag for FAB hiding on mobile
    if (selectedCard) {
      localStorage.setItem('messages_conversation_open', 'true');
    } else {
      localStorage.removeItem('messages_conversation_open');
    }
    
    // Dispatch custom event for immediate reactivity
    window.dispatchEvent(new CustomEvent('messagesConversationChanged'));
  }, [selectedCard]);

  // Update selectedCard when communicationCards change (for real-time updates)
  useEffect(() => {
    const selectedCardId = selectedCardIdRef.current;
    if (selectedCardId && communicationCards.length > 0) {
      const updatedCard = communicationCards.find(card => card.id === selectedCardId);
      const currentSelectedCard = selectedCard;
      
      if (updatedCard && currentSelectedCard) {
        // Check if the number of messages has changed (indicating new messages)
        const hasNewMessages = updatedCard.messages.length !== currentSelectedCard.messages.length;
        const lastMessageChanged = updatedCard.last_message.id !== currentSelectedCard.last_message.id;
        
        console.log('[REALTIME] Checking for updates to selected card:', selectedCardId, {
          hasNewMessages,
          lastMessageChanged,
          currentMessages: currentSelectedCard.messages.length,
          updatedMessages: updatedCard.messages.length,
          currentLastMessage: currentSelectedCard.last_message.id,
          updatedLastMessage: updatedCard.last_message.id
        });
        
        if (hasNewMessages || lastMessageChanged) {
          console.log('[REALTIME] ✅ Updating selected card with new messages:', updatedCard.id, 
            `${currentSelectedCard.messages.length} -> ${updatedCard.messages.length} messages`);
          setSelectedCard(updatedCard);
        }
      }
    }
  }, [communicationCards]); // Remove selectedCard from dependency array to avoid stale closures

  // Filter cards based on active tab and apply pagination
  const allFilteredCards = communicationCards.filter(card => 
    activeTab === 'unread' ? card.has_unread : !card.has_unread
  );
  
  const filteredCards = allFilteredCards.slice(0, pipelineOffset);
  const hasMoreCards = allFilteredCards.length > pipelineOffset;
  
  // Debug logging for Load More button
  console.log('📊 Pipeline pagination debug:', {
    totalCards: communicationCards.length,
    allFilteredCards: allFilteredCards.length,
    filteredCards: filteredCards.length,
    pipelineOffset,
    hasMoreCards,
    activeTab
  });

  const unreadCount = communicationCards.reduce((total, card) => total + card.unread_count, 0);

  // Helper function to extract email address from full email format "Name <email@domain.com>"
  const extractEmailAddress = (contactInfo: string): string => {
    const emailMatch = contactInfo.match(/<([^>]+)>/);
    return emailMatch ? emailMatch[1] : contactInfo;
  };

  const CommunicationCardComponent = ({ card }: { card: CommunicationCard }) => {
    // For communication cards, show just the email address for email contacts, full info for others
    const displayName = card.customer_name || 
      (card.type === 'email' ? extractEmailAddress(card.contact_info) : card.contact_info);
    const messagePreview = card.last_message.subject || card.last_message.body;
    const timeAgo = new Date(card.last_message.created_at).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });

    const isSelected = selectedCard?.id === card.id;
    
    // Check if this customer is a supplier
    const customer = card.customer_id ? customers.find(c => c.id === card.customer_id) : null;
    const isSupplier = customer?.is_supplier || false;

    const handleMarkAllRead = (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent card selection when clicking the button
      markAllMessagesAsRead(card);
    };

    return (
      <div 
        className={`cursor-pointer transition-all duration-200 hover:bg-gray-50/80 ${
          isSelected ? 'bg-blue-50/50 border-l-4 border-blue-500' : ''
        }`}
        onClick={() => {
          setSelectedCard(card);
          if (isMobile) {
            setMobileModalOpen(true);
          }
        }}
      >
        <div className="px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex items-center space-x-3">
            {/* Avatar - Modern circular design with better colors */}
            <div className={`flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-white font-semibold text-sm ${
              card.type === 'customer' ? 'bg-purple-500' :
              card.type === 'phone' ? (
                card.last_message.message_type === 'call' ? (
                  card.last_message.direction === 'outbound' ? 'bg-blue-500' : 
                  (card.last_message.call_status === 'answered' ? 'bg-green-500' : 'bg-red-500')
                ) : 'bg-green-500'
              ) : 'bg-indigo-500'
            }`}>
              {card.type === 'customer' || card.customer_name ? (
                // Show initials for customers
                <span className="uppercase">
                  {card.customer_name ? 
                    card.customer_name.split(' ').map(n => n[0]).join('').slice(0, 2) :
                    displayName.split(' ').map(n => n[0]).join('').slice(0, 2)
                  }
                </span>
              ) : card.type === 'phone' ? (
                card.last_message.message_type === 'call' ? (
                  <Phone className="h-5 w-5 sm:h-6 sm:w-6" />
                ) : (
                  <MessageCircle className="h-5 w-5 sm:h-6 sm:w-6" />
                )
              ) : (
                <Mail className="h-5 w-5 sm:h-6 sm:w-6" />
              )}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2 min-w-0 flex-1">
                  <h3 className="font-semibold text-gray-900 truncate">
                    {displayName}
                  </h3>
                  {isSupplier && (
                    <span className="hidden sm:inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-700">
                      <Building2 className="h-3 w-3 mr-1" />
                      Supplier
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-2 flex-shrink-0">
                  <span className="text-sm text-gray-500 font-medium">{timeAgo}</span>
                  {card.unread_count > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full min-w-[1.5rem] text-center">
                      {card.unread_count}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Message preview */}
              <p className="text-gray-600 truncate mt-1 text-sm">
                {card.last_message?.message_type === 'email' 
                  ? getEmailPreview(card.last_message.body, 80)
                  : messagePreview
                }
              </p>

              {/* Message type indicators */}
              <div className="flex items-center justify-between mt-2">
                <div className="flex items-center space-x-2">
                  <span className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${
                    card.last_message.message_type === 'sms' ? 'bg-green-100 text-green-700' :
                    card.last_message.message_type === 'call' ? (
                      card.last_message.direction === 'outbound' ? 'bg-blue-100 text-blue-700' : 
                      (card.last_message.call_status === 'answered' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700')
                    ) : 'bg-purple-100 text-purple-700'
                  }`}>
                    {card.last_message.message_type === 'sms' ? (
                      <>
                        <MessageCircle className="h-3 w-3 mr-1" />
                        SMS
                      </>
                    ) : card.last_message.message_type === 'call' ? (
                      <>
                        <Phone className="h-3 w-3 mr-1" />
                        {card.last_message.call_status === 'answered' ? 'Call' : 'Missed call'}
                      </>
                    ) : (
                      <>
                        <Mail className="h-3 w-3 mr-1" />
                        Email
                      </>
                    )}
                  </span>
                </div>
                
                {/* Mark Read Button */}
                {card.unread_count > 0 && (
                  <button
                    onClick={handleMarkAllRead}
                    className="text-xs bg-green-500 hover:bg-green-600 text-white font-medium px-3 py-1.5 rounded-md transition-all duration-200 hover:shadow-sm active:scale-95"
                    title={`Mark all ${card.unread_count} message${card.unread_count !== 1 ? 's' : ''} as read`}
                  >
                    Mark Read
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const ConversationView = ({ card, markMessageAsRead, locallyReadMessages, fetchMilestones, onMobileBack }: { 
    card: CommunicationCard; 
    markMessageAsRead: (messageId: string) => Promise<void>;
    locallyReadMessages: Set<string>;
    fetchMilestones: (customerId: string, setLoading?: (loading: boolean) => void) => Promise<void>;
    onMobileBack?: () => void;
  }) => {
    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isFirstRenderRef = useRef(true);
    
    // Timeline states
    const [timelineItems, setTimelineItems] = useState<TimelineItem[]>([]);
    const [timelineOffset, setTimelineOffset] = useState(0);
    const [hasMoreTimeline, setHasMoreTimeline] = useState(false);
    const [loadingMoreTimeline, setLoadingMoreTimeline] = useState(false);
    
    // Message input and UI states
    const [messageInput, setMessageInput] = useState('');
    const [isSending, setIsSending] = useState(false);
    const [sendError, setSendError] = useState<string | null>(null);
    const [replyMethod, setReplyMethod] = useState<'sms' | 'email'>('sms');
    const [isUserTyping, setIsUserTyping] = useState(false);
    const [newMessageNotification, setNewMessageNotification] = useState(false);
    const [showScrollToBottom, setShowScrollToBottom] = useState(false);
    
    // Removed AI toolbar states - now using Nexus assistant
    
    const displayName = card.customer_name || card.contact_info;
    
    const TIMELINE_PAGE_SIZE = 15;

    // Memoized customer lookup to prevent repeated find operations
    const currentCustomer = useMemo(() => {
      if (card.type === 'customer' && card.customer_id) {
        return customers.find(c => c.id === card.customer_id);
      }
      return null;
    }, [card.type, card.customer_id, customers]);

    // NEW: Unified timeline fetching function
    const fetchTimelineItems = async (offset: number = 0, limit: number = TIMELINE_PAGE_SIZE): Promise<{ items: TimelineItem[], hasMore: boolean }> => {
      try {
        if (!selectedBusiness?.id || !supabase) {
          return { items: [], hasMore: false };
        }

        // Get all messages for this conversation
        let messageQuery = supabase
          .from('all_messages')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('created_at', { ascending: false });

        // Apply conversation-specific filters
        if (card.type === 'customer' && card.customer_id) {
          messageQuery = messageQuery.eq('customer_id', card.customer_id);
        } else {
          messageQuery = messageQuery.is('customer_id', null);
          if (card.type === 'phone') {
            messageQuery = messageQuery.eq('message_type', 'sms')
                                     .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          } else if (card.type === 'email') {
            messageQuery = messageQuery.eq('message_type', 'email')
                                      .or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
          }
        }

        // Get all milestones for customer conversations
        let allMilestones: BookingMilestone[] = [];
        if (card.type === 'customer' && card.customer_id) {
          try {
            allMilestones = await getCustomerMilestones(card.customer_id);
          } catch (error) {
            console.error('Error fetching milestones:', error);
            // Continue without milestones rather than failing entirely
          }
        }

        // Get call history for this conversation
        let callQuery = supabase
          .from('call_history')
          .select('*')
          .eq('business_id', selectedBusiness.id)
          .order('call_time', { ascending: false });

        // Apply same conversation-specific filters for calls
        if (card.type === 'customer' && card.customer_id) {
          callQuery = callQuery.eq('customer_id', card.customer_id);
        } else if (card.type === 'phone') {
          callQuery = callQuery.or(`from_number.eq.${card.contact_info},to_number.eq.${card.contact_info}`);
        }

        const [messagesResult, callsResult] = await Promise.all([
          messageQuery,
          callQuery
        ]);

        if (messagesResult.error) throw messagesResult.error;
        if (callsResult.error) throw callsResult.error;

        const allMessages = messagesResult.data || [];
        const allCalls = callsResult.data || [];

        // Combine messages, calls, and milestones into unified timeline
        const combinedTimeline: TimelineItem[] = [];

        // Add messages to timeline
        allMessages.forEach(message => {
          combinedTimeline.push({
            id: `message_${message.id}`,
            type: 'message',
            created_at: message.created_at,
            message
          });
        });

        // Add calls to timeline (convert to message format)
        allCalls.forEach(call => {
          // Match customer by phone number if customer_id is not set
          let matchedCustomerId = call.customer_id;
          
          if (!matchedCustomerId) {
            const callPhoneNumber = call.direction === 'inbound' ? call.from_number : call.to_number;
            const matchedCustomer = customers.find(customer => {
              if (!customer.phone) return false;
              const customerPhone = customer.phone.replace(/\D/g, '');
              const callPhone = callPhoneNumber.replace(/\D/g, '');
              return customerPhone === callPhone || 
                     customerPhone === callPhone.substring(1) || 
                     `1${customerPhone}` === callPhone;
            });
            
            if (matchedCustomer) {
              matchedCustomerId = matchedCustomer.id;
            }
          }

          const callMessage: Message = {
            id: call.id,
            business_id: call.business_id,
            customer_id: matchedCustomerId,
            message_type: 'call',
            direction: call.direction,
            from_number: call.from_number,
            to_number: call.to_number,
            body: call.direction === 'outbound' ? 'Outbound call' : 
              (call.status === 'answered' ? 'Call answered' : 'Missed call'),
            read: 'read',
            created_at: call.call_time,
            updated_at: call.updated_at || call.call_time,
            call_status: call.status,
            duration_seconds: call.duration_seconds,
            call_sid: call.call_sid,
            viewed: call.viewed
          };

          combinedTimeline.push({
            id: `call_${call.id}`,
            type: 'message',
            created_at: call.call_time,
            message: callMessage
          });
        });

        // Add milestones to timeline
        allMilestones.forEach(milestone => {
          combinedTimeline.push({
            id: `milestone_${milestone.id}`,
            type: 'milestone',
            created_at: milestone.created_at,
            milestone
          });
        });

        // Sort by date (newest first)
        combinedTimeline.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

        // Apply pagination to the combined timeline
        const paginatedItems = combinedTimeline.slice(offset, offset + limit);
        const hasMore = combinedTimeline.length > offset + limit;

        console.log(`📅 Timeline: Loaded ${paginatedItems.length} items (offset: ${offset}, total: ${combinedTimeline.length})`);
        
        return { items: paginatedItems, hasMore };
      } catch (error) {
        console.error('Error fetching timeline items:', error);
        return { items: [], hasMore: false };
      }
    };

    // Load initial timeline items when conversation opens
    useEffect(() => {
      const loadInitialTimeline = async () => {
        console.log('🔄 Loading initial timeline for:', card.id);
        const result = await fetchTimelineItems(0, TIMELINE_PAGE_SIZE);
        
        // Reverse items for display (oldest first in UI)
        const reversedItems = [...result.items].reverse();
        setTimelineItems(reversedItems);
        setTimelineOffset(TIMELINE_PAGE_SIZE);
        setHasMoreTimeline(result.hasMore);
        
        // Ensure scroll to bottom after timeline loads
        setTimeout(() => {
          if (messagesContainerRef.current) {
            console.log('📜 Auto-scrolling to bottom after timeline load');
            messagesContainerRef.current.scrollIntoView({ behavior: 'instant', block: 'end' });
          }
        }, 100); // Slightly longer delay to ensure content is rendered
      };

      loadInitialTimeline();
    }, [card.id, selectedBusiness?.id]);

    // Load more timeline items function
    const loadMoreTimeline = async () => {
      if (loadingMoreTimeline || !hasMoreTimeline) return;
      
      setLoadingMoreTimeline(true);
      
      try {
        console.log('📅 Loading more timeline items...');
        const result = await fetchTimelineItems(timelineOffset, TIMELINE_PAGE_SIZE);
        
        if (result.items.length > 0) {
          // Reverse new items and prepend to current timeline (oldest first for display)
          const reversedNewItems = [...result.items].reverse();
          setTimelineItems(prev => [...reversedNewItems, ...prev]);
          setTimelineOffset(prev => prev + TIMELINE_PAGE_SIZE);
          setHasMoreTimeline(result.hasMore);
          
          console.log(`✅ Added ${result.items.length} more timeline items`);
        } else {
          setHasMoreTimeline(false);
        }
      } catch (error) {
        console.error('Error loading more timeline items:', error);
      } finally {
        setLoadingMoreTimeline(false);
      }
    };

    // Seamless message refresh function (updated for timeline approach)
    const refreshConversationMessages = async () => {
      if (!selectedBusiness?.id || !supabase) return;
      
      // Skip refresh if user is actively typing to avoid disruption
      if (isUserTyping) {
        console.log('[REFRESH] ⏸️ Skipping refresh - user is typing');
        return;
      }
      
      try {
        // Refresh current timeline items
        const result = await fetchTimelineItems(0, timelineOffset);
        const reversedItems = [...result.items].reverse();
        
        // Check for new items
        const currentItemIds = new Set(timelineItems.map(item => item.id));
        const newItems = result.items.filter(item => !currentItemIds.has(item.id));
        
        if (newItems.length > 0) {
          console.log('[REFRESH] 🔄 Found new timeline items:', newItems.length);
          setTimelineItems(reversedItems);
          setHasMoreTimeline(result.hasMore);
          
          // Show notification for new inbound messages only
          const newInboundMessages = newItems.filter(item => 
            item.type === 'message' && item.message?.direction === 'inbound'
          );
          
          if (newInboundMessages.length > 0) {
            setNewMessageNotification(true);
            setTimeout(() => setNewMessageNotification(false), 3000);
          }
        }
      } catch (error) {
        console.error('[REFRESH] Error in refresh function:', error);
      }
    };

    // Consolidated conversation initialization when card changes
    useEffect(() => {
      // Reset render state
      isFirstRenderRef.current = true;
      setNewMessageNotification(false);
      setShowScrollToBottom(false);
      setLoadingMoreTimeline(false);
      
      // Clear inputs and errors
      setMessageInput('');
      setSendError(null);
      
      // Set reply method based on last message type
      setReplyMethod(card.last_message.message_type);
    }, [card.id, card.last_message.message_type]);

    // Clear input and error state when switching conversations
    useEffect(() => {
      setMessageInput('');
      setSendError(null);
      
      // Set reply method based on last message type
      setReplyMethod(card.last_message.message_type);
    }, [card.id, card.last_message.message_type]);

    // Handle scroll to detect if user is at top (since we're using column-reverse)
    const handleScroll = () => {
      if (messagesContainerRef.current) {
        const container = messagesContainerRef.current;
        // With column-reverse, being at "top" means scrollTop is near 0
        const isNearTop = container.scrollTop <= 100;
        
        if (isNearTop) {
          setShowScrollToBottom(false);
        } else {
          setShowScrollToBottom(true);
        }
      }
    };

    // Scroll to bottom function (which is actually scrollTop = 0 with column-reverse)
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0;
        setShowScrollToBottom(false);
      }
    };

    // Add scroll listener to messages container
    useEffect(() => {
      const container = messagesContainerRef.current;
      if (container) {
        container.addEventListener('scroll', handleScroll);
        return () => container.removeEventListener('scroll', handleScroll);
      }
    }, []);

    // Handle typing detection for SMS input
    const handleMessageInputChange = (value: string) => {
      setMessageInput(value);
      
      // Set typing state
      setIsUserTyping(true);
      
      // Clear existing timeout
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      
      // Set new timeout to stop typing state after 2 seconds of inactivity
      typingTimeoutRef.current = setTimeout(() => {
        setIsUserTyping(false);
      }, 2000);
    };

    // Clear error when user starts typing
    useEffect(() => {
      if (messageInput && sendError) {
        setSendError(null);
      }
    }, [messageInput, sendError]);

    // Handle SMS sending
    const handleSendSMS = async (aiGenerated: boolean = false) => {
      if (!messageInput.trim()) {
        setSendError('Please enter a message');
        return;
      }

      if (!selectedBusiness?.id) {
        setSendError('No business selected');
        return;
      }

      // Determine the phone number to send to
      let phoneNumber = '';
      if (card.type === 'customer') {
        phoneNumber = currentCustomer?.phone || '';
      } else if (card.type === 'phone') {
        phoneNumber = card.contact_info;
      }

      if (!phoneNumber) {
        setSendError('No phone number available for this contact');
        return;
      }

      setIsSending(true);
      setSendError(null);

      try {
        const result = await sendSMS({
          businessId: selectedBusiness.id,
          to: phoneNumber,
          message: messageInput.trim(),
          aiGenerated
        });

        if (result.success) {
          // Clear the input - realtime subscription will handle the message update
          setMessageInput('');
          
          // Mark all messages in this conversation as read since user replied
          markAllMessagesAsRead(card);
        } else {
          setSendError(result.error || 'Failed to send SMS');
        }
      } catch (error) {
        console.error('Error sending SMS:', error);
        setSendError(error instanceof Error ? error.message : 'Failed to send SMS');
      } finally {
        setIsSending(false);
      }
    };

    // Handle email sending success
    const handleEmailSendSuccess = (messageId: string) => {
      console.log('📧 Email sent successfully:', messageId);
      setSendError(null);
      
      // Mark all messages in this conversation as read since user replied
      markAllMessagesAsRead(card);
      
      // Realtime subscription will handle showing the new message
    };

    // Handle email sending error
    const handleEmailSendError = (error: string) => {
      console.error('📧 Email send failed:', error);
      setSendError(error);
    };

    // Get email address for sending
    const getEmailAddress = () => {
      if (card.type === 'customer') {
        return currentCustomer?.email || '';
      } else if (card.type === 'email') {
        return card.contact_info;
      }
      return '';
    };

    // Generate default email subject based on conversation
    const getDefaultEmailSubject = () => {
      // Get email messages from current timeline
      const emailMessages = timelineItems
        .filter(item => item.type === 'message' && item.message?.message_type === 'email' && item.message?.subject)
        .map(item => item.message!)
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
      
      const lastEmailMessage = emailMessages[0];
      
      if (lastEmailMessage?.subject) {
        const subject = lastEmailMessage.subject;
        // Add "Re: " if it doesn't already have it
        if (!subject.toLowerCase().startsWith('re:')) {
          return `Re: ${subject}`;
        }
        return subject;
      }
      
      return 'Reply from your booking';
    };

    // Get available reply methods based on card type and customer contact info
    const getAvailableReplyMethods = () => {
      if (card.type === 'customer') {
        const methods = [];
        if (currentCustomer?.phone) methods.push('sms');
        if (currentCustomer?.email) methods.push('email');
        return methods;
      } else if (card.type === 'phone') {
        return ['sms'];
      } else if (card.type === 'email') {
        return ['email'];
      }
      return [];
    };

    const availableReplyMethods = getAvailableReplyMethods();

    // Update reply method if current selection is not available
    useEffect(() => {
      if (availableReplyMethods.length > 0 && !availableReplyMethods.includes(replyMethod)) {
        // Set to the last message type if available, otherwise first available method
        if (availableReplyMethods.includes(card.last_message.message_type)) {
          setReplyMethod(card.last_message.message_type);
        } else {
          setReplyMethod(availableReplyMethods[0] as 'sms' | 'email');
        }
      }
    }, [card.id, availableReplyMethods, replyMethod, card.last_message.message_type]);

    // Email composer props
    const emailAddress = getEmailAddress();
    const emailSubject = getDefaultEmailSubject();
    const isEmailDisabled = !selectedBusiness?.id || !emailAddress;

          return (
        <div className="messages-conversation-view flex flex-col h-full bg-white overflow-hidden">
        {/* Conversation Header - Fixed and compact on mobile */}
        <div className="flex-shrink-0 px-2 py-2 sm:px-6 sm:py-5 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2 sm:space-x-4">
            {/* Mobile Back Button */}
            {onMobileBack && (
              <button
                onClick={onMobileBack}
                className="md:hidden p-1.5 hover:bg-gray-100 rounded-lg transition-all duration-200 active:scale-95 flex-shrink-0"
              >
                <span className="sr-only">Back to conversations</span>
                ←
              </button>
            )}
            
            <div className="flex-shrink-0 w-8 h-8 sm:w-12 sm:h-12 bg-white rounded-lg sm:rounded-2xl flex items-center justify-center shadow-sm border border-gray-200">
              {card.type === 'customer' ? (
                <User className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
              ) : card.type === 'phone' ? (
                card.last_message.message_type === 'call' ? (
                  <Phone className={`h-4 w-4 sm:h-6 sm:w-6 ${
                    card.last_message.direction === 'outbound' ? 'text-blue-600' : 
                    (card.last_message.call_status === 'answered' ? 'text-green-600' : 'text-red-600')
                  }`} />
                ) : (
                  <MessageCircle className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
                )
              ) : (
                <Mail className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
              )}
            </div>
                        <div className="flex-1 min-w-0">
              {card.type === 'customer' ? (
                /* Customer card - show name and available contact info */
                <>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    <button
                      onClick={() => {
                        if (card.customer_id) {
                          // Find the full customer object from the customers array (not currentCustomer which is MessageCustomer)
                          const fullCustomer = customers.find(c => c.id === card.customer_id);
                          if (fullCustomer) {
                            handleEditCustomer(fullCustomer);
                          }
                        }
                      }}
                      className="text-lg sm:text-xl font-bold text-gray-900 hover:text-blue-600 transition-colors cursor-pointer truncate"
                      title="Edit customer"
                    >
                      {displayName}
                    </button>
                    {currentCustomer?.is_supplier && (
                      <span className="hidden sm:flex items-center space-x-1 bg-orange-100 text-orange-700 text-sm font-semibold px-3 py-1 rounded-full flex-shrink-0">
                        <Building2 className="h-4 w-4" />
                        <span>Supplier</span>
                      </span>
                    )}
                  </div>
                </>
              ) : (
                /* Phone or Email only card - show just the contact info */
                <>
                  <div className="flex items-center space-x-2 sm:space-x-3">
                    {card.type === 'phone' ? (
                      <MessageCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
                    ) : (
                      <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-purple-500" />
                    )}
                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 truncate">
                      {card.type === 'email' ? extractEmailAddress(card.contact_info) : card.contact_info}
                    </h3>
                    <button
                      onClick={() => handleCreateCustomer(card)}
                      className="p-1.5 sm:p-2 hover:bg-gray-100 rounded-lg sm:rounded-xl transition-all duration-200 active:scale-95"
                      title="Create customer"
                    >
                      <Plus className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 hover:text-gray-700" />
                    </button>
                  </div>
                </>
              )}
            </div>
            {/* Desktop Close Button */}
            <button
              onClick={() => setSelectedCard(null)}
              className="hidden md:block p-2 hover:bg-gray-100 rounded-xl transition-all duration-200 active:scale-95 flex-shrink-0"
            >
              <span className="sr-only">Close conversation</span>
              ✕
            </button>
          </div>
        </div>

        {/* Messages - Scrollable with compact mobile padding */}
        <div ref={messagesContainerRef} className="messages-scrollable flex-1 overflow-y-auto overflow-x-hidden px-2 py-2 sm:px-6 sm:py-6 relative flex flex-col-reverse">
          {/* Skeleton loading during initial render */}
          {isFirstRenderRef.current && timelineItems.length === 0 && (
            <div className="space-y-6 animate-pulse">
              {/* Skeleton messages */}
              {[1, 2, 3].map((i) => (
                <div key={`skeleton-${i}`} className={`flex ${i % 2 === 0 ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-xs lg:max-w-md px-5 py-4 rounded-2xl ${
                    i % 2 === 0 ? 'bg-blue-100' : 'bg-gray-100'
                  }`}>
                    <div className="space-y-3">
                      <div className="h-3 bg-gray-300 rounded w-16"></div>
                      <div className="h-4 bg-gray-300 rounded w-full"></div>
                      <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                      <div className="h-3 bg-gray-300 rounded w-20"></div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          
          {/* Actual content */}
          {(!isFirstRenderRef.current || timelineItems.length > 0) && (
            <>
              {/* New Message Notification */}
              {newMessageNotification && (
                <div className="absolute top-6 left-1/2 transform -translate-x-1/2 z-10 bg-blue-500 text-white px-5 py-3 rounded-2xl shadow-lg animate-pulse">
                  <div className="flex items-center space-x-2">
                    <MessageCircle className="h-5 w-5" />
                    <span className="text-sm font-semibold">New message received</span>
                  </div>
                </div>
              )}
              
              {/* Milestone loading is now completely transparent and non-blocking - no UI indicators needed */}
              
              {/* Timeline items in reverse order for column-reverse layout */}
              {[...timelineItems].reverse().map((item, index) => {
                if (item.type === 'message' && item.message) {
                  const message = item.message;
                  const isOutbound = message.direction === 'outbound';
                  const messageTime = new Date(message.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
                  
                  // Check if message is unread (not outbound AND not read in DB AND not locally marked as read)
                  const isUnread = !isOutbound && (
                    (message.read === 'unread' && !locallyReadMessages.has(message.id)) ||
                    (message.message_type === 'call' && message.call_status === 'missed' && message.viewed === false)
                  );
                  
                  return (
                    <div
                      key={item.id}
                      className={`flex ${isOutbound ? 'justify-end' : 'justify-start'} mb-4 sm:mb-6`}
                    >
                      <div className="relative">
                        <div
                          className={`max-w-[280px] sm:max-w-xs lg:max-w-md px-3 py-3 sm:px-5 sm:py-4 rounded-xl sm:rounded-2xl shadow-sm ${
                            isOutbound
                              ? 'bg-blue-500 text-white'
                              : 'bg-gray-50 text-gray-900 border border-gray-200'
                          }`}
                        >
                          {/* Message Type & Subject - More compact on mobile */}
                          <div className="flex items-center space-x-1.5 sm:space-x-2 mb-1.5 sm:mb-2">
                            {message.message_type === 'sms' ? (
                              <MessageCircle className="h-3 w-3 sm:h-4 sm:w-4" />
                            ) : message.message_type === 'call' ? (
                              <Phone className={`h-3 w-3 sm:h-4 sm:w-4 ${
                                message.direction === 'outbound' ? 'text-white' : 
                                (message.call_status === 'answered' ? 'text-green-500' : 'text-red-500')
                              }`} />
                            ) : (
                              <Mail className="h-3 w-3 sm:h-4 sm:w-4" />
                            )}
                            <span className="text-xs font-semibold uppercase tracking-wider">
                              {message.message_type === 'call' ? 
                                (message.direction === 'outbound' ? 'Call' : 
                                 (message.call_status === 'answered' ? 'Call' : 'Missed')) : 
                                message.message_type}
                            </span>
                            {/* AI Label - only show for outbound messages that used AI */}
                            {isOutbound && message.ai_response && (
                              <span className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 text-xs font-semibold px-2 py-0.5 sm:px-3 sm:py-1 rounded-full border border-blue-200">
                                AI
                              </span>
                            )}
                          </div>
                          
                          {/* Email Subject */}
                          {message.subject && (
                            <div className="font-semibold text-sm sm:text-base mb-1.5 sm:mb-2">
                              {message.subject}
                            </div>
                          )}
                          
                          {/* Message Body */}
                          {message.message_type === 'email' ? (
                            <EmailDisplay 
                              content={message.body}
                              subject={message.subject}
                              className="text-xs sm:text-sm"
                              isOutbound={isOutbound}
                            />
                          ) : message.message_type === 'call' ? (
                            <div className="text-xs sm:text-sm leading-relaxed">
                              <span>{message.body}</span>
                            </div>
                          ) : (
                            <div className="text-xs sm:text-sm leading-relaxed whitespace-pre-wrap break-words">
                              {message.body}
                            </div>
                          )}
                          
                          {/* Timestamp */}
                          <div className={`text-xs font-medium mt-2 sm:mt-3 ${
                            isOutbound ? 'text-blue-100' : 'text-gray-500'
                          }`}>
                            {messageTime}
                          </div>
                        </div>
                        
                        {/* Unread Badge - "Unread" text badge positioned outside the message bubble */}
                        {isUnread && (
                          <button
                            onClick={() => markMessageAsRead(message.id)}
                            className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full transition-all duration-200 active:scale-95 cursor-pointer shadow-lg"
                            title="Mark as read"
                          >
                            Unread
                          </button>
                        )}
                      </div>
                    </div>
                  );
                } else if (item.type === 'milestone' && item.milestone) {
                  const milestone = item.milestone;
                  const milestoneTime = new Date(milestone.created_at).toLocaleString(undefined, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit',
                    hour12: true
                  });
                  
                  // Helper function to get milestone icon - professional neutral styling
                  const getMilestoneIcon = (type: string, title?: string) => {
                    const iconClass = "h-5 w-5 text-slate-600";
                    
                    switch (type) {
                      case 'booking_scheduled':
                        return <Calendar className={iconClass} />;
                      case 'booking_completed':
                        return <CheckCircle className={iconClass} />;
                      case 'proposal_created':
                        return <FileText className={iconClass} />;
                      case 'custom':
                        // Check milestone title to determine icon
                        if (title?.includes('Proposal Sent')) {
                          return <Mail className={iconClass} />;
                        } else if (title?.includes('Proposal Viewed')) {
                          return <Eye className={iconClass} />;
                        } else if (title?.includes('Proposal Approved')) {
                          return <CheckCircle className={iconClass} />;
                        }
                        return <CheckCircle className={iconClass} />;
                      default:
                        return <CheckCircle className={iconClass} />;
                    }
                  };
                  
                  // Helper function to get milestone color - professional neutral styling
                  const getMilestoneColor = () => {
                    return 'bg-slate-50 text-slate-700 border-slate-200 shadow-sm hover:shadow-md';
                  };
                  
                  return (
                    <div key={item.id} className="flex justify-end mb-4 sm:mb-6">
                      <div 
                        className={`max-w-[280px] sm:max-w-sm px-3 py-3 sm:px-5 sm:py-4 rounded-xl sm:rounded-2xl border cursor-pointer transition-all duration-200 active:scale-95 ${getMilestoneColor()}`}
                        onClick={() => handleMilestoneClick(milestone)}
                        title={milestone.milestone.metadata?.entity_type === 'proposal' ? 'Click to view proposal details' : 'Click to view booking details'}
                      >
                        <div className="flex items-start space-x-3 sm:space-x-4">
                          <div className="flex-shrink-0 mt-0.5 sm:mt-1">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-slate-100 rounded-xl sm:rounded-2xl flex items-center justify-center border border-slate-200">
                              {getMilestoneIcon(milestone.milestone.type, milestone.milestone.title)}
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm sm:text-base text-slate-800 mb-1.5 sm:mb-2">
                              {milestone.milestone.title}
                            </div>
                            {milestone.milestone.description && (
                              <div className="text-xs sm:text-sm text-slate-600 leading-relaxed mb-2 sm:mb-3">
                                {milestone.milestone.description}
                              </div>
                            )}
                            <div className="text-xs sm:text-sm text-slate-500 font-semibold">
                              {milestoneTime}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }
                
                return null;
              })}
              
              {/* Scroll to Bottom Button - Hidden on mobile when conversation is open */}
              {showScrollToBottom && (
                <button
                  onClick={scrollToBottom}
                  className={`fixed bottom-32 right-8 bg-blue-500 hover:bg-blue-600 text-white p-4 rounded-2xl shadow-lg z-20 transition-all duration-200 active:scale-95 animate-bounce ${
                    selectedCard ? 'hidden md:block' : 'block'
                  }`}
                  title="Scroll to bottom"
                >
                  <ChevronDown className="h-6 w-6" />
                </button>
              )}
              
              {/* Show More Messages Button - at END of DOM to appear at TOP visually with column-reverse */}
              {hasMoreTimeline && (
                <div className="flex justify-center py-4">
                  <button
                    onClick={loadMoreTimeline}
                    disabled={loadingMoreTimeline}
                    className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white px-6 py-3 rounded-2xl text-sm font-semibold transition-all duration-200 active:scale-95 flex items-center space-x-3 shadow-lg"
                  >
                    {loadingMoreTimeline ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Show More Messages</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Message Input Area - Fixed at bottom, compact on mobile */}
        <div className="messages-input-area flex-shrink-0 px-2 py-2 sm:px-6 sm:py-5 border-t border-gray-200 bg-gray-50 shadow-lg">
          {/* Integrated Message Input with Toolbar and Internal Reply Type Toggles */}
          <div className="bg-white border border-gray-200 shadow-sm overflow-hidden">
            {/* Nexus Assistant Toolbar */}
            <div className="border-b border-gray-100 bg-gray-50/30 px-4 py-2">
              <NexusMessagesAssistant
                businessId={selectedBusiness?.id || ''}
                contactInfo={card.contact_info}
                messageType={card.type === 'email' ? 'email' : 'sms'}
                customerId={card.customer_id}
                isVisible={true}
                onQuickActionExecuted={(action, result) => {
                  console.log('Nexus quick action executed:', action, result);
                  // Handle quick action results here
                }}
                onOpenBookingModal={(booking) => {
                  console.log('Opening payments modal from AI Tools:', booking.id);
                  setSelectedBookingForPayments(booking);
                  setBetaPaymentsModalOpen(true);
                }}
              />
            </div>
            
            {/* Main Input Area - In the middle */}
            <div className="relative">
              {replyMethod === 'email' ? (
                /* Email Reply Composer */
                <div className="w-full">
                  <EmailReplyComposer
                    businessId={selectedBusiness?.id || ''}
                    to={emailAddress}
                    defaultSubject={emailSubject}
                    placeholder="Type your email message here..."
                    onSendSuccess={handleEmailSendSuccess}
                    onSendError={handleEmailSendError}
                    disabled={isEmailDisabled}
                    className="w-full rounded-none border-0"
                    conversationId={card.id}
                    customerId={card.customer_id}
                    shouldFetchAI={false}

                  />
                </div>
              ) : (
                /* SMS Reply Input with AI Suggestions */
                <SMSReplyComposer
                  businessId={selectedBusiness?.id || ''}
                  value={messageInput}
                  onChange={handleMessageInputChange}
                  onSend={handleSendSMS}
                  isSending={isSending}
                  error={sendError}
                  conversationId={card.id}
                  customerId={card.customer_id}
                  phoneNumber={card.type === 'phone' ? card.contact_info : undefined}
                  shouldFetchAI={false}
                  className="w-full rounded-none border-0"

                />
              )}
            </div>
            
            {/* Reply Type Toggles - On the underside of reply box, full width 50/50 split */}
            {availableReplyMethods.length > 1 && (
              <div className="border-t border-gray-100 bg-gray-50/30">
                <div className="flex w-full">
                  <button
                    onClick={() => setReplyMethod('sms')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-semibold transition-all duration-200 active:scale-95 ${
                      replyMethod === 'sms'
                        ? 'bg-green-100 text-green-700 border-t-2 border-green-500'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50/50'
                    }`}
                  >
                    <MessageCircle className="h-4 w-4" />
                    <span>SMS</span>
                  </button>
                  <button
                    onClick={() => setReplyMethod('email')}
                    className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 text-sm font-semibold transition-all duration-200 active:scale-95 ${
                      replyMethod === 'email'
                        ? 'bg-purple-100 text-purple-700 border-t-2 border-purple-500'
                        : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50/50'
                    }`}
                  >
                    <Mail className="h-4 w-4" />
                    <span>Email</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Mobile conversation modal component
  const MobileConversationModal: React.FC<{
    isOpen: boolean;
    onClose: () => void;
    card: CommunicationCard | null;
    markMessageAsRead: (messageId: string) => Promise<void>;
    locallyReadMessages: Set<string>;
    fetchMilestones: (customerId: string) => Promise<void>;
  }> = ({ isOpen, onClose, card, markMessageAsRead, locallyReadMessages, fetchMilestones }) => {
    if (!isOpen || !card) return null;

    return createPortal(
      <div className="fixed inset-0 z-[9999] md:hidden">
        {/* Full screen modal container */}
        <div className="fixed inset-0 bg-white flex flex-col h-full">
          <ConversationView 
            card={card} 
            markMessageAsRead={markMessageAsRead}
            locallyReadMessages={locallyReadMessages}
            fetchMilestones={fetchMilestones}
            onMobileBack={onClose}
          />
        </div>
      </div>,
      document.body
    );
  };

  if (loading) {
    return (
      <div 
        className="bg-gray-100 flex items-center justify-center overflow-hidden p-0 sm:p-6 h-[calc(100vh-8rem)]" 
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
        }}
      >
        <div className="bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 sm:h-10 sm:w-10 border-b-2 border-purple-600 mx-auto mb-4 sm:mb-6"></div>
          <p className="text-gray-500 font-medium text-sm sm:text-base">Loading messages...</p>
        </div>
      </div>
    );
  }

  // Get back button text and navigation based on source
  const getBackButtonInfo = () => {
    switch (sourcePage) {
      case 'proposals':
        return { text: 'Back to Proposals', path: '/proposals' };
      case 'crm':
        return { text: 'Back to CRM', path: '/crm' };
      case 'calendar':
        return { text: 'Back to Calendar', path: '/calendarV2' };
      case 'bookings':
        return { text: 'Back to Bookings', path: '/bookings' };
      default:
        return { text: 'Back to CRM', path: '/crm' };
    }
  };

  const backButtonInfo = getBackButtonInfo();

  return (
    <div 
      className="messages-container bg-gray-100 flex flex-col h-[calc(100vh-3rem)]" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif'
      }}
    >

      {/* Main Content - Two Column Layout */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Message Pipeline */}
        <div className={`${
          selectedCard ? 'hidden md:flex' : 'flex'
        } w-full md:w-96 lg:w-[500px] bg-gray-50 flex-col h-full overflow-hidden`}>
          {/* Pipeline Header - More compact on mobile */}
          <div className="flex-shrink-0 px-2 py-2 sm:px-6 sm:py-5 border-b border-gray-200 bg-white">
            {/* Back Arrow - Show based on where we came from */}
            {sourcePage && !cameFromCRM && !cameFromBooking && (
              <button
                onClick={() => navigate(backButtonInfo.path)}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm font-medium">{backButtonInfo.text}</span>
              </button>
            )}
            {cameFromCRM && (
              <button
                onClick={handleBackToCRM}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm font-medium">Back to CRM</span>
              </button>
            )}
            {cameFromBooking && (
              <button
                onClick={handleBackToBooking}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 transition-colors duration-200"
              >
                <ArrowLeft className="h-4 w-4" />
                <span className="text-sm font-medium">Back to Booking</span>
              </button>
            )}
            <h2 className="text-lg sm:text-2xl font-bold text-gray-900">Messages</h2>
            <p className="text-sm sm:text-base text-gray-500 mt-0.5 sm:mt-1 font-medium hidden sm:block">Manage your conversations</p>
          </div>

          {/* Pipeline Tabs - Modern design */}
          <div className="flex-shrink-0 p-3 sm:p-4 bg-white">
            <div className="flex bg-gray-100 rounded-lg p-1">
              <button
                onClick={() => setActiveTab('unread')}
                className={`flex-1 px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === 'unread'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <MessageCircle className="h-4 w-4" />
                  <span>Unread</span>
                  {unreadCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full min-w-[1.25rem] text-center">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </button>
              <button
                onClick={() => setActiveTab('read')}
                className={`flex-1 px-3 py-2 sm:px-4 sm:py-2.5 text-sm font-semibold rounded-md transition-all duration-200 ${
                  activeTab === 'read'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>Read</span>
                </div>
              </button>
            </div>
          </div>

          {/* Pipeline Content - Scrollable */}
          <div className="flex-1 overflow-y-auto overflow-x-hidden bg-white">
            {filteredCards.length > 0 ? (
              <>
                <div className="space-y-1">
                  {filteredCards.map((card) => (
                    <CommunicationCardComponent key={card.id} card={card} />
                  ))}
                </div>
                
                {/* Load More Cards Button */}
                {hasMoreCards && (
                  <div className="flex justify-center p-4 border-t border-gray-200 bg-white">
                    <button
                      onClick={loadMoreCards}
                      disabled={loadingMoreCards}
                      className="bg-blue-500 hover:bg-blue-600 disabled:bg-blue-300 text-white font-semibold px-6 py-3 text-sm transition-all duration-200 active:scale-95 flex items-center space-x-3 shadow-sm"
                    >
                      {loadingMoreCards ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-2 border-gray-400 border-t-transparent"></div>
                          <span>Loading...</span>
                        </>
                      ) : (
                        <span>Load More ({allFilteredCards.length - pipelineOffset} remaining)</span>
                      )}
                    </button>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full px-2 py-8 sm:px-6 sm:py-16">
                <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 shadow-sm border border-gray-200">
                  {activeTab === 'unread' ? (
                    <Mail className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                  ) : (
                    <MessageCircle className="h-8 w-8 sm:h-10 sm:w-10 text-gray-400" />
                  )}
                </div>
                <p className="text-gray-500 text-center font-medium text-base sm:text-lg">
                  No {activeTab} messages
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Right Side - Conversation View */}
        <div className={`${
          selectedCard && !isMobile ? 'flex' : 'hidden md:flex'
        } flex-1 flex-col h-full`}>
          {selectedCard && (
            <ConversationView 
              card={selectedCard} 
              markMessageAsRead={markMessageAsRead}
              locallyReadMessages={locallyReadMessages}
              fetchMilestones={fetchCustomerMilestones}
            />
          )}
          {!selectedCard && (
            /* No Conversation Selected State */
            <div className="flex-1 flex items-center justify-center px-3 py-0 sm:px-6">
              <div className="text-center max-w-md bg-gray-50 rounded-xl sm:rounded-2xl shadow-lg border border-gray-200 p-6 sm:p-12">
                <div className="w-16 h-16 sm:w-24 sm:h-24 bg-gray-100 rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-sm border border-gray-200">
                  <MessageCircle className="h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
                </div>
                <h3 className="text-lg sm:text-2xl font-bold text-gray-900 mb-2 sm:mb-3">
                  No Conversation Selected
                </h3>
                <p className="text-gray-500 font-medium text-sm sm:text-lg">
                  Select a conversation from the pipeline to view messages here
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Customer Modals */}
      {selectedBusiness && (
        <>
          <EditCustomerModal
            isOpen={editCustomerModalOpen}
            onClose={() => {
              setEditCustomerModalOpen(false);
              setSelectedCustomerForEdit(null);
            }}
            onSuccess={handleCustomerSuccess}
            customer={selectedCustomerForEdit}
          />
          
          <CustomerModal
            isOpen={createCustomerModalOpen}
            onClose={() => {
              setCreateCustomerModalOpen(false);
              setCreateCustomerInitialData({});
            }}
            onSuccess={handleCustomerSuccess}
            businessId={selectedBusiness.id}
            initialData={createCustomerInitialData}
          />
          
          <BookingInfoModal
            isOpen={bookingInfoModalOpen}
            onClose={() => {
              setBookingInfoModalOpen(false);
              setSelectedBooking(null);
            }}
            booking={selectedBooking}
            onUpdate={handleBookingUpdate}
            readOnly={false}
          />
          
          {/* Beta Payments Modal */}
          {selectedBookingForPayments && (
            <BetaPaymentsModal
              isOpen={betaPaymentsModalOpen}
              onClose={() => {
                setBetaPaymentsModalOpen(false);
                setSelectedBookingForPayments(null);
              }}
              bookingId={selectedBookingForPayments.id}
              bookingNumber={typeof selectedBookingForPayments.booking_number === 'number' 
                ? selectedBookingForPayments.booking_number 
                : parseInt(String(selectedBookingForPayments.booking_number)) || 0}
              bookingData={{
                total_amount: selectedBookingForPayments.total_amount || 0,
                customer: selectedBookingForPayments.customer ? {
                  id: selectedBookingForPayments.customer.id,
                  first_name: selectedBookingForPayments.customer.first_name || '',
                  last_name: selectedBookingForPayments.customer.last_name || '',
                  email: selectedBookingForPayments.customer.email || undefined,
                  phone: selectedBookingForPayments.customer.phone || undefined,
                  full_name: `${selectedBookingForPayments.customer.first_name || ''} ${selectedBookingForPayments.customer.last_name || ''}`.trim() || undefined
                } : undefined,
                payments: selectedBookingForPayments.payments || []
              }}
              booking={selectedBookingForPayments}
              onUpdate={() => {
                // Refresh messages if needed
                console.log('Payments updated');
              }}
            />
          )}
          
          {selectedProposal && (
            <ProposalDetailsModal
              isOpen={proposalDetailsModalOpen}
              onClose={() => {
                setProposalDetailsModalOpen(false);
                setSelectedProposal(null);
              }}
              proposal={selectedProposal}
              onUpdate={handleProposalUpdate}
            />
          )}
        </>
      )}
      
      {/* Mobile Conversation Modal */}
      <MobileConversationModal
        isOpen={mobileModalOpen}
        onClose={() => {
          setMobileModalOpen(false);
          setSelectedCard(null);
        }}
        card={selectedCard}
        markMessageAsRead={markMessageAsRead}
        locallyReadMessages={locallyReadMessages}
        fetchMilestones={fetchCustomerMilestones}
      />
    </div>
  );
} 